import * as React from 'react';
import { useEffect, useState } from 'react';
import Alert from '@mui/material/Alert';
import Autocomplete from '@mui/material/Autocomplete';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import ListItemText from '@mui/material/ListItemText';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import DeleteForeverRoundedIcon from '@mui/icons-material/DeleteForeverRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import ManageAccountsRoundedIcon from '@mui/icons-material/ManageAccountsRounded';
import RestartAltRoundedIcon from '@mui/icons-material/RestartAltRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import SwapHorizRoundedIcon from '@mui/icons-material/SwapHorizRounded';
import AdminLayout, { AdminSection } from '../../components/AdminLayout';
import axiosInstance from '../../utils/axiosInstance';
import { getBusinessRoleLabel } from '../../utils/userProfile';

const emptyFilters = {
  search: '',
  status: '',
  membership_state: '',
};

const userStatusOptions = [
  { value: '', label: 'All statuses' },
  { value: 'active', label: 'Active' },
  { value: 'pending_login', label: 'Pending Login' },
  { value: 'invited', label: 'Invited' },
  { value: 'inactive', label: 'Inactive' },
  { value: 'suspended', label: 'Suspended' },
];

const membershipStateOptions = [
  { value: '', label: 'All placements' },
  { value: 'assigned', label: 'Assigned' },
  { value: 'unassigned', label: 'No memberships' },
  { value: 'inactive_or_unassigned', label: 'Deleted/inactive or no memberships' },
];

const roleOptions = [
  { value: 'partner_user', label: 'User' },
  { value: 'partner_admin', label: 'Admin' },
];

function getUserLabel(user) {
  return user?.full_name || user?.email || user?.cognito_sub || 'Unnamed user';
}

function getUserInitials(user) {
  const source = getUserLabel(user);
  const parts = source.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  }
  return source.slice(0, 2).toUpperCase();
}

function getStatusColor(status) {
  if (status === 'active') {
    return 'success';
  }
  if (status === 'invited' || status === 'pending_login') {
    return 'info';
  }
  if (status === 'suspended') {
    return 'warning';
  }
  return 'default';
}

function getCognitoStatusColor(status) {
  const normalized = String(status || '').toUpperCase();
  if (normalized === 'CONFIRMED') {
    return 'success';
  }
  if (normalized === 'FORCE_CHANGE_PASSWORD' || normalized === 'RESET_REQUIRED') {
    return 'warning';
  }
  if (normalized === 'UNCONFIRMED') {
    return 'info';
  }
  return 'default';
}

function getCognitoUserLabel(user) {
  return user?.full_name || user?.email || user?.username || user?.cognito_sub || 'Unnamed Cognito user';
}

function getCognitoUserInitials(user) {
  const source = getCognitoUserLabel(user);
  const parts = source.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  }
  return source.slice(0, 2).toUpperCase();
}

function formatDate(value) {
  if (!value) {
    return 'Never';
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return String(value);
  }
  return date.toLocaleString();
}

function getPartnerLabel(partner) {
  return partner?.business_unit_display_name
    || partner?.partner_display_name
    || partner?.display_name
    || partner?.business_unit_code
    || partner?.partner_code
    || partner?.partner_id
    || 'Unknown partner';
}

function getPartnerSecondary(partner) {
  return [
    partner?.partner_code,
    'Partner',
    partner?.status,
  ].filter(Boolean).join(' - ');
}

function getPartnerOptionLabel(partner) {
  const code = partner?.partner_code ? ` (${partner.partner_code})` : '';
  return `${getPartnerLabel(partner)}${code}`;
}

function getDirectoryStateLabel(user) {
  if (user?.directory_state === 'deleted_or_unassigned') {
    return 'Deleted/inactive with no membership';
  }
  if (user?.directory_state === 'unassigned') {
    return 'No membership';
  }
  return 'Assigned';
}

function getInviteLabel(invite) {
  return invite?.full_name || invite?.email || invite?.invite_id || 'Pending invite';
}

function getInviteStatusColor(status) {
  if (status === 'pending') {
    return 'info';
  }
  if (status === 'expired') {
    return 'warning';
  }
  return 'default';
}

function summarizeMembership(membership) {
  return [
    getPartnerLabel(membership),
    getBusinessRoleLabel(membership?.role),
  ].filter(Boolean).join(' - ');
}

export default function AdminUsersPage(props) {
  const [filters, setFilters] = useState(emptyFilters);
  const [state, setState] = useState({ loading: true, error: '', data: null });
  const [partnerOptions, setPartnerOptions] = useState([]);
  const [partnerOptionsLoading, setPartnerOptionsLoading] = useState(false);
  const [partnerOptionsError, setPartnerOptionsError] = useState('');
  const [feedback, setFeedback] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [editingUser, setEditingUser] = useState(null);
  const [userEditForm, setUserEditForm] = useState({ full_name: '', status: 'active' });
  const [savingUserEdit, setSavingUserEdit] = useState(false);
  const [placingUser, setPlacingUser] = useState(null);
  const [placementForm, setPlacementForm] = useState({
    source_partner_id: '',
    target_partner_id: '',
    role: 'partner_user',
    status: '',
  });
  const [savingPlacement, setSavingPlacement] = useState(false);
  const [removingUser, setRemovingUser] = useState(null);
  const [removePartnerId, setRemovePartnerId] = useState('');
  const [savingRemoval, setSavingRemoval] = useState(false);
  const [deletingUser, setDeletingUser] = useState(null);
  const [savingUserDelete, setSavingUserDelete] = useState(false);
  const [editingInvite, setEditingInvite] = useState(null);
  const [inviteEditForm, setInviteEditForm] = useState({
    target_partner_id: '',
    full_name: '',
    role: 'partner_user',
  });
  const [savingInviteEdit, setSavingInviteEdit] = useState(false);
  const [resendingInviteId, setResendingInviteId] = useState('');
  const [revokingInvite, setRevokingInvite] = useState(null);
  const [savingInviteRevoke, setSavingInviteRevoke] = useState(false);
  const [cognitoOrphanState, setCognitoOrphanState] = useState({ loading: true, error: '', data: null });
  const [deletingCognitoOrphan, setDeletingCognitoOrphan] = useState(null);
  const [savingCognitoOrphanDelete, setSavingCognitoOrphanDelete] = useState(false);

  async function loadUsers(nextFilters = filters) {
    setState((current) => ({ ...current, loading: true, error: '' }));
    try {
      const response = await axiosInstance.get('/admin/users', { params: nextFilters });
      setState({ loading: false, error: '', data: response.data });
    } catch (error) {
      setState({
        loading: false,
        error: error?.response?.data?.message || 'Unable to load AMX users.',
        data: null,
      });
    }
  }

  async function loadCognitoOrphans() {
    setCognitoOrphanState((current) => ({ ...current, loading: true, error: '' }));
    try {
      const response = await axiosInstance.get('/admin/cognito-orphans');
      setCognitoOrphanState({ loading: false, error: '', data: response.data });
    } catch (error) {
      setCognitoOrphanState({
        loading: false,
        error: error?.response?.data?.message || 'Unable to load Cognito-only users.',
        data: null,
      });
    }
  }

  useEffect(() => {
    loadUsers(emptyFilters);
    loadCognitoOrphans();
  }, []);

  useEffect(() => {
    let isActive = true;

    async function loadPartnerOptions() {
      setPartnerOptionsLoading(true);
      setPartnerOptionsError('');
      try {
        const response = await axiosInstance.get('/admin/partners');
        if (!isActive) {
          return;
        }
        setPartnerOptions(Array.isArray(response.data?.partners) ? response.data.partners : []);
      } catch (error) {
        if (!isActive) {
          return;
        }
        setPartnerOptions([]);
        setPartnerOptionsError(error?.response?.data?.message || 'Unable to load partner targets.');
      } finally {
        if (isActive) {
          setPartnerOptionsLoading(false);
        }
      }
    }

    loadPartnerOptions();

    return () => {
      isActive = false;
    };
  }, []);

  function updateFilter(field, value) {
    setFilters((current) => ({ ...current, [field]: value }));
  }

  function applyFilters(event) {
    event.preventDefault();
    loadUsers(filters);
  }

  function resetFilters() {
    setFilters(emptyFilters);
    loadUsers(emptyFilters);
  }

  function openUserEditor(user) {
    setEditingUser(user);
    setUserEditForm({
      full_name: user?.full_name || '',
      status: user?.status || 'active',
    });
    setFeedback('');
    setErrorMessage('');
  }

  function closeUserEditor() {
    if (!savingUserEdit) {
      setEditingUser(null);
    }
  }

  async function saveUserEdit(event) {
    event.preventDefault();
    if (!editingUser) {
      return;
    }
    setSavingUserEdit(true);
    setFeedback('');
    setErrorMessage('');
    try {
      await axiosInstance.patch(`/admin/users/${editingUser.cognito_sub}`, userEditForm);
      setFeedback(`Updated ${getUserLabel(editingUser)}.`);
      setEditingUser(null);
      await loadUsers(filters);
    } catch (error) {
      setErrorMessage(error?.response?.data?.message || 'Unable to update user.');
    } finally {
      setSavingUserEdit(false);
    }
  }

  function openPlacementDialog(user) {
    const firstMembership = user.memberships?.[0] || null;
    setPlacingUser(user);
    setPlacementForm({
      source_partner_id: firstMembership?.partner_id || '',
      target_partner_id: '',
      role: firstMembership?.role || 'partner_user',
      status: user.status === 'inactive' ? 'active' : '',
    });
    setFeedback('');
    setErrorMessage('');
  }

  function closePlacementDialog() {
    if (!savingPlacement) {
      setPlacingUser(null);
    }
  }

  async function savePlacement(event) {
    event.preventDefault();
    if (!placingUser) {
      return;
    }
    if (!placementForm.target_partner_id) {
      setErrorMessage('Select a target partner.');
      return;
    }
    setSavingPlacement(true);
    setFeedback('');
    setErrorMessage('');
    try {
      const payload = {
        target_partner_id: placementForm.target_partner_id,
        role: placementForm.role,
      };
      if (placementForm.source_partner_id) {
        payload.source_partner_id = placementForm.source_partner_id;
      }
      if (placementForm.status) {
        payload.status = placementForm.status;
      }
      const response = await axiosInstance.post(`/admin/users/${placingUser.cognito_sub}/placement`, payload);
      setFeedback(`Placed ${getUserLabel(placingUser)} in ${getPartnerLabel(response.data?.placement?.target_partner)}.`);
      setPlacingUser(null);
      await loadUsers(filters);
    } catch (error) {
      setErrorMessage(error?.response?.data?.message || 'Unable to place user.');
    } finally {
      setSavingPlacement(false);
    }
  }

  function openRemoveDialog(user) {
    const firstMembership = user.memberships?.[0] || null;
    setRemovingUser(user);
    setRemovePartnerId(firstMembership?.partner_id || '');
    setFeedback('');
    setErrorMessage('');
  }

  function closeRemoveDialog() {
    if (!savingRemoval) {
      setRemovingUser(null);
      setRemovePartnerId('');
    }
  }

  async function removeMembership() {
    if (!removingUser || !removePartnerId) {
      setErrorMessage('Select a membership to remove.');
      return;
    }
    setSavingRemoval(true);
    setFeedback('');
    setErrorMessage('');
    try {
      await axiosInstance.delete(`/admin/partners/${removePartnerId}/users/${removingUser.cognito_sub}`);
      setFeedback(`Removed a membership from ${getUserLabel(removingUser)}.`);
      setRemovingUser(null);
      setRemovePartnerId('');
      await loadUsers(filters);
    } catch (error) {
      setErrorMessage(error?.response?.data?.message || 'Unable to remove membership.');
    } finally {
      setSavingRemoval(false);
    }
  }

  function openDeleteUserDialog(user) {
    setDeletingUser(user);
    setFeedback('');
    setErrorMessage('');
  }

  function closeDeleteUserDialog() {
    if (!savingUserDelete) {
      setDeletingUser(null);
    }
  }

  async function deleteUserCompletely() {
    if (!deletingUser) {
      return;
    }
    const label = getUserLabel(deletingUser);
    setSavingUserDelete(true);
    setFeedback('');
    setErrorMessage('');
    try {
      await axiosInstance.delete(`/admin/users/${deletingUser.cognito_sub}`);
      setFeedback(`Deleted ${label} from AMX.`);
      setDeletingUser(null);
      await loadUsers(filters);
    } catch (error) {
      setErrorMessage(error?.response?.data?.message || 'Unable to delete user from AMX.');
    } finally {
      setSavingUserDelete(false);
    }
  }

  function openInviteEditor(invite) {
    setEditingInvite(invite);
    setInviteEditForm({
      target_partner_id: invite?.partner_id || '',
      full_name: invite?.full_name || '',
      role: invite?.role || 'partner_user',
    });
    setFeedback('');
    setErrorMessage('');
  }

  function closeInviteEditor() {
    if (!savingInviteEdit) {
      setEditingInvite(null);
    }
  }

  async function saveInviteEdit(event) {
    event.preventDefault();
    if (!editingInvite) {
      return;
    }
    if (!inviteEditForm.target_partner_id) {
      setErrorMessage('Select a target partner.');
      return;
    }

    setSavingInviteEdit(true);
    setFeedback('');
    setErrorMessage('');
    try {
      const response = await axiosInstance.patch(`/admin/invites/${editingInvite.invite_id}`, {
        target_partner_id: inviteEditForm.target_partner_id,
        full_name: inviteEditForm.full_name || null,
        role: inviteEditForm.role,
      });
      setFeedback(`Updated invite for ${getInviteLabel(response.data?.invite || editingInvite)}.`);
      setEditingInvite(null);
      await loadUsers(filters);
    } catch (error) {
      setErrorMessage(error?.response?.data?.message || 'Unable to update invite.');
    } finally {
      setSavingInviteEdit(false);
    }
  }

  async function resendInvite(invite) {
    if (!invite?.invite_id) {
      return;
    }
    setResendingInviteId(invite.invite_id);
    setFeedback('');
    setErrorMessage('');
    try {
      await axiosInstance.post(`/admin/invites/${invite.invite_id}/resend`);
      setFeedback(`Resent invite to ${invite.email}.`);
      await loadUsers(filters);
    } catch (error) {
      setErrorMessage(error?.response?.data?.message || 'Unable to resend invite.');
    } finally {
      setResendingInviteId('');
    }
  }

  function openRevokeInviteDialog(invite) {
    setRevokingInvite(invite);
    setFeedback('');
    setErrorMessage('');
  }

  function closeRevokeInviteDialog() {
    if (!savingInviteRevoke) {
      setRevokingInvite(null);
    }
  }

  async function revokeInvite() {
    if (!revokingInvite?.invite_id) {
      return;
    }
    const label = getInviteLabel(revokingInvite);
    setSavingInviteRevoke(true);
    setFeedback('');
    setErrorMessage('');
    try {
      await axiosInstance.delete(`/admin/invites/${revokingInvite.invite_id}`);
      setFeedback(`Revoked invite for ${label}.`);
      setRevokingInvite(null);
      await loadUsers(filters);
    } catch (error) {
      setErrorMessage(error?.response?.data?.message || 'Unable to revoke invite.');
    } finally {
      setSavingInviteRevoke(false);
    }
  }

  function openDeleteCognitoOrphanDialog(user) {
    setDeletingCognitoOrphan(user);
    setFeedback('');
    setErrorMessage('');
  }

  function closeDeleteCognitoOrphanDialog() {
    if (!savingCognitoOrphanDelete) {
      setDeletingCognitoOrphan(null);
    }
  }

  async function deleteCognitoOrphanCompletely() {
    if (!deletingCognitoOrphan?.username) {
      setErrorMessage('Cognito username is required.');
      return;
    }
    const label = getCognitoUserLabel(deletingCognitoOrphan);
    setSavingCognitoOrphanDelete(true);
    setFeedback('');
    setErrorMessage('');
    try {
      await axiosInstance.delete(`/admin/cognito-orphans/${encodeURIComponent(deletingCognitoOrphan.username)}`);
      setFeedback(`Deleted ${label} from Cognito.`);
      setDeletingCognitoOrphan(null);
      await loadCognitoOrphans();
    } catch (error) {
      setErrorMessage(error?.response?.data?.message || 'Unable to delete Cognito user.');
    } finally {
      setSavingCognitoOrphanDelete(false);
    }
  }

  const users = Array.isArray(state.data?.users) ? state.data.users : [];
  const pendingInvites = Array.isArray(state.data?.pending_invites) ? state.data.pending_invites : [];
  const summary = state.data?.summary || {};
  const cognitoOrphans = Array.isArray(cognitoOrphanState.data?.cognito_orphans)
    ? cognitoOrphanState.data.cognito_orphans
    : [];
  const cognitoOrphanSummary = cognitoOrphanState.data?.summary || {};
  const selectedTargetPartner = partnerOptions.find((partner) => partner.partner_id === placementForm.target_partner_id) || null;
  const selectedInviteTargetPartner = partnerOptions.find((partner) => partner.partner_id === inviteEditForm.target_partner_id) || null;
  const selectedRemoveMembership = (removingUser?.memberships || []).find((membership) => membership.partner_id === removePartnerId) || null;

  return (
    <AdminLayout
      props={props}
      breadcrumbItems={[
        { label: 'AMX xConsole', href: '/' },
        { label: 'Admin Dashboard', href: '/admin' },
        { label: 'Users' },
      ]}
      title="AMX User Directory"
      subtitle="Review every AMX user account except AMX admins, including deleted/inactive users and accounts without partner memberships."
    >
      {feedback ? <Alert severity="success">{feedback}</Alert> : null}
      {errorMessage ? <Alert severity="error">{errorMessage}</Alert> : null}

      <AdminSection title="Directory Filters" subtitle="Search by user, partner, business unit, or Cognito sub.">
        <Stack spacing={2}>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Chip label={`${summary.total || 0} users`} variant="outlined" />
            <Chip label={`${summary.assigned || 0} assigned`} variant="outlined" />
            <Chip label={`${summary.unassigned || 0} no memberships`} variant="outlined" />
            <Chip label={`${summary.pending_invites || 0} pending invites`} color={pendingInvites.length ? 'info' : 'default'} variant="outlined" />
            <Chip label={`${summary.inactive || 0} inactive`} variant="outlined" />
            <Chip label={`${summary.deleted_or_unassigned || 0} deleted/inactive with no membership`} variant="outlined" />
            <Chip label="AMX admins excluded" color="primary" variant="outlined" />
          </Stack>

          <Stack component="form" direction={{ xs: 'column', lg: 'row' }} spacing={1.5} onSubmit={applyFilters}>
            <TextField
              label="Search"
              value={filters.search}
              onChange={(event) => updateFilter('search', event.target.value)}
              size="small"
              fullWidth
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchRoundedIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              select
              label="Status"
              value={filters.status}
              onChange={(event) => updateFilter('status', event.target.value)}
              size="small"
              sx={{ minWidth: { xs: '100%', lg: 180 } }}
            >
              {userStatusOptions.map((option) => (
                <MenuItem key={option.value || 'all'} value={option.value}>{option.label}</MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="Placement"
              value={filters.membership_state}
              onChange={(event) => updateFilter('membership_state', event.target.value)}
              size="small"
              sx={{ minWidth: { xs: '100%', lg: 240 } }}
            >
              {membershipStateOptions.map((option) => (
                <MenuItem key={option.value || 'all'} value={option.value}>{option.label}</MenuItem>
              ))}
            </TextField>
            <Button type="submit" variant="contained" startIcon={<SearchRoundedIcon />}>
              Search
            </Button>
            <Button type="button" variant="outlined" startIcon={<RestartAltRoundedIcon />} onClick={resetFilters}>
              Reset
            </Button>
          </Stack>
          {partnerOptionsError ? <Alert severity="warning">{partnerOptionsError}</Alert> : null}
        </Stack>
      </AdminSection>

      {state.error ? <Alert severity="error">{state.error}</Alert> : null}

      {state.loading ? (
        <Stack direction="row" spacing={2} alignItems="center">
          <CircularProgress size={24} />
          <Typography>Loading AMX users...</Typography>
        </Stack>
      ) : (
        <AdminSection title="Users" subtitle="Use placement to move assigned users or assign users with no current membership.">
          <Box sx={{ overflowX: 'auto' }}>
            <Table size="small" sx={{ minWidth: 1260 }}>
              <TableHead>
                <TableRow>
                  <TableCell>User</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Placement</TableCell>
                  <TableCell>Activity</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {users.length ? users.map((user) => (
                  <TableRow key={user.cognito_sub} hover>
                    <TableCell sx={{ minWidth: 320 }}>
                      <Stack direction="row" spacing={1.5} alignItems="center">
                        <Avatar sx={{ width: 36, height: 36, fontSize: '0.85rem', fontWeight: 700 }}>
                          {getUserInitials(user)}
                        </Avatar>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="body2" sx={{ fontWeight: 700, wordBreak: 'break-word' }}>
                            {getUserLabel(user)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', wordBreak: 'break-all' }}>
                            {user.email || user.cognito_sub}
                          </Typography>
                        </Box>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Stack spacing={0.75} alignItems="flex-start">
                        <Chip size="small" color={getStatusColor(user.status)} label={user.status || 'unknown'} variant="outlined" />
                        <Typography variant="caption" color="text.secondary">
                          {getDirectoryStateLabel(user)}
                        </Typography>
                      </Stack>
                    </TableCell>
                    <TableCell sx={{ minWidth: 360 }}>
                      {(user.memberships || []).length ? (
                        <Stack spacing={1}>
                          {user.memberships.map((membership) => (
                            <Box key={`${user.cognito_sub}-${membership.partner_id}`}>
                              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                {getPartnerLabel(membership)}
                              </Typography>
                              <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                                <Chip size="small" variant="outlined" label={membership.partner_code || membership.partner_id} />
                                <Chip size="small" variant="outlined" label={getBusinessRoleLabel(membership.role)} />
                              </Stack>
                            </Box>
                          ))}
                        </Stack>
                      ) : (
                        <Typography variant="body2" color="text.secondary">No partner membership</Typography>
                      )}
                    </TableCell>
                    <TableCell sx={{ minWidth: 220 }}>
                      <Typography variant="body2">{formatDate(user.last_activity_at || user.last_login_at)}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Deactivated {formatDate(user.deactivated_at)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                        <Tooltip title="Manage user">
                          <IconButton size="small" onClick={() => openUserEditor(user)}>
                            <ManageAccountsRoundedIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={(user.memberships || []).length ? 'Move user' : 'Assign user'}>
                          <span>
                            <IconButton
                              size="small"
                              onClick={() => openPlacementDialog(user)}
                              disabled={partnerOptionsLoading || !partnerOptions.length}
                            >
                              <SwapHorizRoundedIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="Remove membership only">
                          <span>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => openRemoveDialog(user)}
                              disabled={!(user.memberships || []).length}
                            >
                              <DeleteOutlineRoundedIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="Delete AMX user">
                          <IconButton size="small" color="error" onClick={() => openDeleteUserDialog(user)}>
                            <DeleteForeverRoundedIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                )) : (
                  <TableRow>
                    <TableCell colSpan={5}>
                      <Typography variant="body2" color="text.secondary">
                        No users match the current filters.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Box>
        </AdminSection>
      )}

      {!state.loading ? (
        <AdminSection title="Pending Invites" subtitle="Invites that have not been accepted yet and do not have partner memberships.">
          <Box sx={{ overflowX: 'auto' }}>
            <Table size="small" sx={{ minWidth: 1120 }}>
              <TableHead>
                <TableRow>
                  <TableCell>Invitee</TableCell>
                  <TableCell>Target</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Role</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {pendingInvites.length ? pendingInvites.map((invite) => (
                  <TableRow key={invite.invite_id} hover>
                    <TableCell sx={{ minWidth: 300 }}>
                      <Typography variant="body2" sx={{ fontWeight: 700, wordBreak: 'break-word' }}>
                        {getInviteLabel(invite)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', wordBreak: 'break-all' }}>
                        {invite.email}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ minWidth: 260 }}>
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>
                        {getPartnerLabel(invite)}
                      </Typography>
                      <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                        <Chip size="small" variant="outlined" label={invite.partner_code || invite.partner_id} />
                        <Chip size="small" variant="outlined" label={invite.business_unit_id ? 'Business Unit' : 'Partner'} />
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Stack spacing={0.75} alignItems="flex-start">
                        <Chip size="small" color={getInviteStatusColor(invite.status)} label={invite.status || 'pending'} variant="outlined" />
                        <Typography variant="caption" color="text.secondary">
                          Expires {formatDate(invite.expires_at)}
                        </Typography>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                        <Chip size="small" variant="outlined" label={getBusinessRoleLabel(invite.role)} />
                      </Stack>
                    </TableCell>
                    <TableCell sx={{ minWidth: 180 }}>{formatDate(invite.created_at)}</TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                        <Tooltip title="Resend invite">
                          <span>
                            <IconButton
                              size="small"
                              onClick={() => resendInvite(invite)}
                              disabled={resendingInviteId === invite.invite_id}
                            >
                              {resendingInviteId === invite.invite_id ? <CircularProgress size={18} /> : <RestartAltRoundedIcon fontSize="small" />}
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="Move or edit invite">
                          <span>
                            <IconButton
                              size="small"
                              onClick={() => openInviteEditor(invite)}
                              disabled={partnerOptionsLoading || !partnerOptions.length}
                            >
                              <SwapHorizRoundedIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="Revoke invite">
                          <IconButton size="small" color="error" onClick={() => openRevokeInviteDialog(invite)}>
                            <DeleteOutlineRoundedIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                )) : (
                  <TableRow>
                    <TableCell colSpan={6}>
                      <Typography variant="body2" color="text.secondary">
                        No pending invites match the current filters.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Box>
        </AdminSection>
      ) : null}

      <AdminSection title="Cognito-Only Users" subtitle="Resolve Cognito accounts that are not linked to any AMX user record.">
        <Stack spacing={2}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'flex-start', sm: 'center' }} justifyContent="space-between">
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Chip label={`${cognitoOrphanSummary.cognito_users || 0} Cognito users`} variant="outlined" />
              <Chip label={`${cognitoOrphanSummary.orphans || 0} Cognito-only users`} color={cognitoOrphans.length ? 'warning' : 'default'} variant="outlined" />
            </Stack>
            <Button
              type="button"
              variant="outlined"
              startIcon={cognitoOrphanState.loading ? <CircularProgress size={16} /> : <RestartAltRoundedIcon />}
              onClick={loadCognitoOrphans}
              disabled={cognitoOrphanState.loading}
            >
              Refresh
            </Button>
          </Stack>

          {cognitoOrphanState.error ? <Alert severity="error">{cognitoOrphanState.error}</Alert> : null}

          {cognitoOrphanState.loading ? (
            <Stack direction="row" spacing={2} alignItems="center">
              <CircularProgress size={24} />
              <Typography>Loading Cognito-only users...</Typography>
            </Stack>
          ) : (
            <Box sx={{ overflowX: 'auto' }}>
              <Table size="small" sx={{ minWidth: 1080 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>Cognito User</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Enabled</TableCell>
                    <TableCell>Created</TableCell>
                    <TableCell>Updated</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {cognitoOrphans.length ? cognitoOrphans.map((user) => (
                    <TableRow key={user.username || user.cognito_sub || user.email} hover>
                      <TableCell sx={{ minWidth: 360 }}>
                        <Stack direction="row" spacing={1.5} alignItems="center">
                          <Avatar sx={{ width: 36, height: 36, fontSize: '0.85rem', fontWeight: 700 }}>
                            {getCognitoUserInitials(user)}
                          </Avatar>
                          <Box sx={{ minWidth: 0 }}>
                            <Typography variant="body2" sx={{ fontWeight: 700, wordBreak: 'break-word' }}>
                              {getCognitoUserLabel(user)}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', wordBreak: 'break-all' }}>
                              {user.email || user.username || '-'}
                            </Typography>
                            {user.username && user.username !== user.email ? (
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', wordBreak: 'break-all' }}>
                                {`Username ${user.username}`}
                              </Typography>
                            ) : null}
                            {user.cognito_sub ? (
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', wordBreak: 'break-all' }}>
                                {user.cognito_sub}
                              </Typography>
                            ) : null}
                          </Box>
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          color={getCognitoStatusColor(user.user_status)}
                          label={user.user_status || 'unknown'}
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          color={user.enabled ? 'success' : 'default'}
                          label={user.enabled ? 'Enabled' : 'Disabled'}
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell sx={{ minWidth: 180 }}>{formatDate(user.created_at)}</TableCell>
                      <TableCell sx={{ minWidth: 180 }}>{formatDate(user.updated_at)}</TableCell>
                      <TableCell align="right">
                        <Tooltip title="Delete Cognito user">
                          <span>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => openDeleteCognitoOrphanDialog(user)}
                              disabled={!user.username}
                            >
                              <DeleteForeverRoundedIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  )) : (
                    <TableRow>
                      <TableCell colSpan={6}>
                        <Typography variant="body2" color="text.secondary">
                          No Cognito-only users found.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </Box>
          )}
        </Stack>
      </AdminSection>

      <Dialog open={Boolean(editingInvite)} onClose={closeInviteEditor} fullWidth maxWidth="md">
        <DialogTitle>Manage Invite</DialogTitle>
        <Box component="form" onSubmit={saveInviteEdit}>
          <DialogContent dividers>
            <Stack spacing={2.5} sx={{ pt: 1 }}>
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, wordBreak: 'break-word' }}>
                  {getInviteLabel(editingInvite)}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ wordBreak: 'break-all' }}>
                  {editingInvite?.email}
                </Typography>
              </Box>

              <Autocomplete
                options={partnerOptions}
                loading={partnerOptionsLoading}
                value={selectedInviteTargetPartner}
                onChange={(_, value) => setInviteEditForm({ ...inviteEditForm, target_partner_id: value?.partner_id || '' })}
                getOptionLabel={getPartnerOptionLabel}
                isOptionEqualToValue={(option, value) => option.partner_id === value.partner_id}
                renderOption={(optionProps, option) => (
                  <li {...optionProps} key={option.partner_id}>
                    <ListItemText primary={getPartnerLabel(option)} secondary={getPartnerSecondary(option)} />
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Target Partner"
                    helperText="Choose the partner where this invite should be accepted."
                    required
                  />
                )}
              />

              <TextField
                label="Full Name"
                value={inviteEditForm.full_name}
                onChange={(event) => setInviteEditForm({ ...inviteEditForm, full_name: event.target.value })}
                fullWidth
              />

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField
                  select
                  label="Role"
                  value={inviteEditForm.role}
                  onChange={(event) => setInviteEditForm({ ...inviteEditForm, role: event.target.value })}
                  fullWidth
                >
                  {roleOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                  ))}
                </TextField>
              </Stack>
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button type="button" onClick={closeInviteEditor} disabled={savingInviteEdit}>Cancel</Button>
            <Button type="submit" variant="contained" disabled={savingInviteEdit || !inviteEditForm.target_partner_id}>
              {savingInviteEdit ? 'Saving...' : 'Save Invite'}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>

      <Dialog open={Boolean(revokingInvite)} onClose={closeRevokeInviteDialog} fullWidth maxWidth="sm">
        <DialogTitle>Revoke Invite</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, wordBreak: 'break-word' }}>
                {getInviteLabel(revokingInvite)}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ wordBreak: 'break-all' }}>
                {revokingInvite?.email}
              </Typography>
            </Box>
            <Alert severity="warning">
              This removes the pending invite. The invite link will no longer be usable.
            </Alert>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeRevokeInviteDialog} disabled={savingInviteRevoke}>Cancel</Button>
          <Button color="error" variant="contained" onClick={revokeInvite} disabled={savingInviteRevoke}>
            {savingInviteRevoke ? 'Revoking...' : 'Revoke Invite'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(editingUser)} onClose={closeUserEditor} fullWidth maxWidth="sm">
        <DialogTitle>Manage User</DialogTitle>
        <Box component="form" onSubmit={saveUserEdit}>
          <DialogContent dividers>
            <Stack spacing={2} sx={{ pt: 1 }}>
              <TextField
                label="Full Name"
                value={userEditForm.full_name}
                onChange={(event) => setUserEditForm({ ...userEditForm, full_name: event.target.value })}
                fullWidth
              />
              <TextField
                select
                label="Status"
                value={userEditForm.status}
                onChange={(event) => setUserEditForm({ ...userEditForm, status: event.target.value })}
                fullWidth
              >
                {userStatusOptions.filter((option) => option.value).map((option) => (
                  <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                ))}
              </TextField>
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button type="button" onClick={closeUserEditor} disabled={savingUserEdit}>Cancel</Button>
            <Button type="submit" variant="contained" disabled={savingUserEdit}>
              {savingUserEdit ? 'Saving...' : 'Save User'}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>

      <Dialog open={Boolean(placingUser)} onClose={closePlacementDialog} fullWidth maxWidth="md">
        <DialogTitle>{placingUser?.membership_count ? 'Move User' : 'Assign User'}</DialogTitle>
        <Box component="form" onSubmit={savePlacement}>
          <DialogContent dividers>
            <Stack spacing={2.5} sx={{ pt: 1 }}>
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, wordBreak: 'break-word' }}>
                  {getUserLabel(placingUser)}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ wordBreak: 'break-all' }}>
                  {placingUser?.email || placingUser?.cognito_sub}
                </Typography>
              </Box>

              {(placingUser?.memberships || []).length ? (
                <TextField
                  select
                  label="Source Membership"
                  value={placementForm.source_partner_id}
                  onChange={(event) => setPlacementForm({ ...placementForm, source_partner_id: event.target.value })}
                  fullWidth
                  required={(placingUser?.memberships || []).length > 1}
                >
                  {(placingUser?.memberships || []).map((membership) => (
                    <MenuItem key={membership.partner_id} value={membership.partner_id}>
                      {summarizeMembership(membership)}
                    </MenuItem>
                  ))}
                </TextField>
              ) : (
                <Alert severity="info">This user has no current partner membership and can be assigned directly.</Alert>
              )}

              <Autocomplete
                options={partnerOptions}
                loading={partnerOptionsLoading}
                value={selectedTargetPartner}
                onChange={(_, value) => setPlacementForm({ ...placementForm, target_partner_id: value?.partner_id || '' })}
                getOptionLabel={getPartnerOptionLabel}
                isOptionEqualToValue={(option, value) => option.partner_id === value.partner_id}
                renderOption={(optionProps, option) => (
                  <li {...optionProps} key={option.partner_id}>
                    <ListItemText primary={getPartnerLabel(option)} secondary={getPartnerSecondary(option)} />
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Target Partner"
                    helperText="Choose the partner where this user should belong."
                    required
                  />
                )}
              />

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField
                  select
                  label="Target Role"
                  value={placementForm.role}
                  onChange={(event) => setPlacementForm({ ...placementForm, role: event.target.value })}
                  fullWidth
                >
                  {roleOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                  ))}
                </TextField>
                <TextField
                  select
                  label="Status After Placement"
                  value={placementForm.status}
                  onChange={(event) => setPlacementForm({ ...placementForm, status: event.target.value })}
                  fullWidth
                >
                  <MenuItem value="">Keep current status</MenuItem>
                  {userStatusOptions.filter((option) => option.value).map((option) => (
                    <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                  ))}
                </TextField>
              </Stack>
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button type="button" onClick={closePlacementDialog} disabled={savingPlacement}>Cancel</Button>
            <Button
              type="submit"
              variant="contained"
              disabled={savingPlacement || !placementForm.target_partner_id}
              startIcon={savingPlacement ? <CircularProgress size={16} /> : <SwapHorizRoundedIcon />}
            >
              {savingPlacement ? 'Saving...' : (placingUser?.membership_count ? 'Move User' : 'Assign User')}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>

      <Dialog open={Boolean(removingUser)} onClose={closeRemoveDialog} fullWidth maxWidth="sm">
        <DialogTitle>Remove Membership</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                {getUserLabel(removingUser)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Removing the last membership will leave the user visible here with no partner placement.
              </Typography>
            </Box>
            <TextField
              select
              label="Membership"
              value={removePartnerId}
              onChange={(event) => setRemovePartnerId(event.target.value)}
              fullWidth
              required
            >
              {(removingUser?.memberships || []).map((membership) => (
                <MenuItem key={membership.partner_id} value={membership.partner_id}>
                  {summarizeMembership(membership)}
                </MenuItem>
              ))}
            </TextField>
            {selectedRemoveMembership ? (
              <Alert severity="warning">
                {`This removes ${getPartnerLabel(selectedRemoveMembership)} from the user account.`}
              </Alert>
            ) : null}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeRemoveDialog} disabled={savingRemoval}>Cancel</Button>
          <Button color="error" variant="contained" onClick={removeMembership} disabled={savingRemoval || !removePartnerId}>
            {savingRemoval ? 'Removing...' : 'Remove Membership'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(deletingUser)} onClose={closeDeleteUserDialog} fullWidth maxWidth="sm">
        <DialogTitle>Delete AMX User</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, wordBreak: 'break-word' }}>
                {getUserLabel(deletingUser)}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ wordBreak: 'break-all' }}>
                {deletingUser?.email || deletingUser?.cognito_sub}
              </Typography>
            </Box>
            <Alert severity="error">
              This permanently deletes the AMX user record, Cognito account, memberships, and AMX data created by this user.
            </Alert>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDeleteUserDialog} disabled={savingUserDelete}>Cancel</Button>
          <Button color="error" variant="contained" onClick={deleteUserCompletely} disabled={savingUserDelete}>
            {savingUserDelete ? 'Deleting...' : 'Delete User'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(deletingCognitoOrphan)} onClose={closeDeleteCognitoOrphanDialog} fullWidth maxWidth="sm">
        <DialogTitle>Delete Cognito User</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, wordBreak: 'break-word' }}>
                {getCognitoUserLabel(deletingCognitoOrphan)}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ wordBreak: 'break-all' }}>
                {deletingCognitoOrphan?.username || deletingCognitoOrphan?.cognito_sub}
              </Typography>
            </Box>
            <Alert severity="error">
              This permanently deletes the Cognito account. It is currently not linked to an AMX user record.
            </Alert>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDeleteCognitoOrphanDialog} disabled={savingCognitoOrphanDelete}>Cancel</Button>
          <Button color="error" variant="contained" onClick={deleteCognitoOrphanCompletely} disabled={savingCognitoOrphanDelete}>
            {savingCognitoOrphanDelete ? 'Deleting...' : 'Delete Cognito User'}
          </Button>
        </DialogActions>
      </Dialog>
    </AdminLayout>
  );
}
